import React, { useState, useEffect } from 'react';
import { getStudentDisplayName } from '../utils/studentName';

/**
 * SimplifiedTranscriptForm - Compact form for creating manual transcripts
 *
 * A streamlined version of ManualTranscriptForm designed for use in drawers/modals.
 * Removes tips section and sample button for a cleaner experience.
 */
const SimplifiedTranscriptForm = ({
  students,
  initialStudentUuid = null,
  onSubmit,
  onCancel,
  disabled = false
}) => {
  const [title, setTitle] = useState('');
  const [transcriptContent, setTranscriptContent] = useState('');
  const [studentUuid, setStudentUuid] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [error, setError] = useState(null);

  // Set default date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSessionDate(today);
  }, []);

  // Keep student field aligned with the currently selected Sessions student.
  useEffect(() => {
    if (initialStudentUuid) {
      setStudentUuid(initialStudentUuid);
    } else {
      setStudentUuid('');
    }
  }, [initialStudentUuid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!title.trim()) {
      setError('Please enter a session title');
      return;
    }
    if (!transcriptContent.trim()) {
      setError('Please enter the transcript content');
      return;
    }

    // Call parent handler with form data
    onSubmit({
      title: title.trim(),
      transcript_content: transcriptContent.trim(),
      student_uuid: studentUuid || null,
      session_date: sessionDate ? `${sessionDate}T12:00:00Z` : null
    });
  };

  return (
    <form className="simplified-transcript-form" onSubmit={handleSubmit}>
      {error && (
        <div className="form-error">
          <span className="error-icon">!</span>
          {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="st-title">Session Title *</label>
        <input
          type="text"
          id="st-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Math Session - Fractions Review"
          disabled={disabled}
          maxLength={200}
          autoFocus
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="st-date">Date</label>
          <input
            type="date"
            id="st-date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            disabled={disabled}
          />
        </div>

        <div className="form-group">
          <label htmlFor="st-student">Student (Optional)</label>
          <select
            id="st-student"
            value={studentUuid}
            onChange={(e) => setStudentUuid(e.target.value)}
            disabled={disabled}
          >
            <option value="">-- None --</option>
            {students && students.map((student) => (
              <option key={student.uuid} value={student.uuid}>
                {getStudentDisplayName(student)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group transcript-group">
        <label htmlFor="st-content">Transcript *</label>
        <textarea
          id="st-content"
          value={transcriptContent}
          onChange={(e) => setTranscriptContent(e.target.value)}
          placeholder="Paste or type the session transcript here...

Format:
Tutor: [tutor dialogue]
Student: [student dialogue]"
          rows={12}
          disabled={disabled}
        />
        {transcriptContent.length > 0 && (
          <span className="char-count">{transcriptContent.length} characters</span>
        )}
      </div>

      <div className="form-actions">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={disabled || !title.trim() || !transcriptContent.trim()}
        >
          {disabled ? 'Creating...' : 'Create Session'}
        </button>
      </div>

      <style>{`
        .simplified-transcript-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .simplified-transcript-form .form-error {
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid var(--error-color, #f44336);
          color: var(--error-color, #f44336);
          padding: 12px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .simplified-transcript-form .error-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--error-color, #f44336);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }

        .simplified-transcript-form .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .simplified-transcript-form .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .simplified-transcript-form .transcript-group {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .simplified-transcript-form label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-color, #fff);
        }

        .simplified-transcript-form input,
        .simplified-transcript-form select,
        .simplified-transcript-form textarea {
          padding: 10px 12px;
          border: 1px solid var(--border-color, #3a3a4e);
          border-radius: 8px;
          background: var(--input-bg, #1a1a2e);
          color: var(--text-color, #fff);
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .simplified-transcript-form input:focus,
        .simplified-transcript-form select:focus,
        .simplified-transcript-form textarea:focus {
          outline: none;
          border-color: var(--primary-color, #6366f1);
        }

        .simplified-transcript-form input:disabled,
        .simplified-transcript-form select:disabled,
        .simplified-transcript-form textarea:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .simplified-transcript-form textarea {
          resize: vertical;
          min-height: 150px;
          flex: 1;
        }

        .simplified-transcript-form .char-count {
          font-size: 12px;
          color: var(--text-muted, #888);
          text-align: right;
          margin-top: 4px;
        }

        .simplified-transcript-form .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 8px;
        }

        .simplified-transcript-form .btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .simplified-transcript-form .btn-primary {
          background: var(--primary-color, #6366f1);
          color: white;
        }

        .simplified-transcript-form .btn-primary:hover:not(:disabled) {
          background: var(--primary-dark, #4f46e5);
        }

        .simplified-transcript-form .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .simplified-transcript-form .btn-secondary {
          background: transparent;
          color: var(--text-muted, #888);
          border: 1px solid var(--border-color, #3a3a4e);
        }

        .simplified-transcript-form .btn-secondary:hover:not(:disabled) {
          background: var(--bg-tertiary, #2a2a3e);
        }

        @media (max-width: 500px) {
          .simplified-transcript-form .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </form>
  );
};

export default SimplifiedTranscriptForm;
